import { Link, useParams } from "react-router-dom";
import { teamMap, teams } from "@/mocks/teams";
import { matches } from "@/mocks/matches";
import { news } from "@/mocks/news";
import TeamLogo from "@/components/base/TeamLogo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MatchCard from "@/components/feature/MatchCard";
import { formatTime, formatDateLabel } from "@/utils/format";

export default function TeamDetail() {
  const { id } = useParams();
  const team = id ? teamMap[id] : undefined;

  if (!team) {
    return (
      <div className="px-4 md:px-8 py-16">
        <div className="mx-auto flex max-w-lg flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
          <i className="ri-error-warning-line text-4xl text-accent-500" />
          <h1 className="mt-3 font-heading text-xl font-bold text-foreground-950">球队不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">未找到该球队资料，请返回选择其他球队。</p>
          <Link
            to="/live"
            className="mt-5 inline-flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" /> 返回直播赛事
          </Link>
        </div>
      </div>
    );
  }

  const teamMatches = matches.filter((m) => m.homeTeamId === team.id || m.awayTeamId === team.id).slice(0, 3);
  const teamNews = news.filter((n) => n.tags.some((tag) => tag.includes(team.name))).slice(0, 4);
  const otherTeams = teams.filter((t) => t.id !== team.id).slice(0, 5);

  return (
    <div className="px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "直播赛事", to: "/live" }, { label: `${team.city}${team.name}` }]} />

      <section
        className="mt-4 overflow-hidden rounded-lg p-6 md:p-8"
        style={{ background: `linear-gradient(120deg, ${team.primaryColor} 0%, ${team.primaryColor} 55%, ${team.secondaryColor} 100%)` }}
      >
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-background-50/90">
            <TeamLogo team={team} size="lg" />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
              {team.city}{team.name}
            </h1>
            <p className="mt-1 text-sm text-background-200">
              {team.conference} · 成立 {team.founded} 年 · 主场 {team.arena}
            </p>
          </div>
          <div className="ml-auto flex gap-3">
            <div className="rounded-md bg-background-50/15 px-4 py-2 text-center">
              <p className="text-[11px] text-background-200">赛季战绩</p>
              <p className="font-heading text-lg font-bold text-background-50">{team.record}</p>
            </div>
          </div>
        </div>
        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-background-100">{team.description}</p>
      </section>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="flex w-7 h-7 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-group-line" />
              </span>
              当前阵容与核心球员
            </h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[460px] text-left text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-xs text-foreground-500">
                    <th className="py-2 pr-3 font-medium">号码</th>
                    <th className="py-2 pr-3 font-medium">球员</th>
                    <th className="py-2 pr-3 font-medium">位置</th>
                    <th className="py-2 pr-3 text-right font-medium">场均得分</th>
                    <th className="py-2 pr-3 text-right font-medium">篮板</th>
                    <th className="py-2 text-right font-medium">助攻</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {team.players.map((player) => (
                    <tr key={player.name} className="text-foreground-800">
                      <td className="py-2.5 pr-3 font-heading font-bold text-primary-600">#{player.number}</td>
                      <td className="py-2.5 pr-3 font-medium text-foreground-950">{player.name}</td>
                      <td className="py-2.5 pr-3 text-foreground-600">{player.position}</td>
                      <td className="py-2.5 pr-3 text-right">{player.points.toFixed(1)}</td>
                      <td className="py-2.5 pr-3 text-right">{player.rebounds.toFixed(1)}</td>
                      <td className="py-2.5 text-right">{player.assists.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-[11px] text-foreground-400">球员与数据为虚构演示内容。</p>
          </section>

          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-950">近期赛事</h2>
              <Link to="/schedule" className="text-sm font-semibold text-primary-600 hover:text-primary-700">查看赛程</Link>
            </div>
            {teamMatches.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {teamMatches.map((m) => <MatchCard key={m.id} match={m} />)}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-10 text-center text-sm text-foreground-500">
                暂无可展示的赛事安排
              </div>
            )}
          </section>
        </div>

        <aside className="flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="flex w-7 h-7 items-center justify-center rounded-md bg-accent-500 text-background-50">
                <i className="ri-newspaper-line" />
              </span>
              相关新闻
            </h2>
            {teamNews.length > 0 ? (
              <ul className="mt-4 flex flex-col gap-3">
                {teamNews.map((article) => (
                  <li key={article.id}>
                    <Link
                      to={`/news/${article.id}`}
                      className="group flex gap-3 rounded-md border border-background-100 p-2.5 transition-colors hover:border-primary-200 hover:bg-background-100"
                    >
                      <div className="h-14 w-20 shrink-0 overflow-hidden rounded bg-background-200">
                        <img src={article.image} alt={article.title} title={`${article.title} - NBA直播`} className="h-full w-full object-top" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 group-hover:text-primary-600">{article.title}</h3>
                        <p className="mt-1 text-[11px] text-foreground-500">{article.date}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-4 text-sm text-foreground-500">暂无相关新闻</p>
            )}
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-lg font-bold text-foreground-950">其他球队</h2>
            <div className="mt-4 flex flex-col gap-2">
              {otherTeams.map((t) => (
                <Link
                  key={t.id}
                  to={`/team/${t.id}`}
                  className="flex items-center gap-3 rounded-md border border-background-100 px-3 py-2 transition-colors hover:border-primary-200 hover:bg-background-100"
                >
                  <TeamLogo team={t} size="sm" />
                  <span className="text-sm font-medium text-foreground-800">{t.city}{t.name}</span>
                  <i className="ri-arrow-right-s-line ml-auto text-foreground-400" />
                </Link>
              ))}
            </div>
          </section>

          <Link
            to="/live"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
          >
            <i className="ri-live-line" /> 返回直播赛事
          </Link>
        </aside>
      </div>

      <p className="mt-8 text-xs text-foreground-400">
        最近一场比赛时间：{formatDateLabel(teamMatches[0]?.startTime ?? matches[0].startTime)} {formatTime(teamMatches[0]?.startTime ?? matches[0].startTime)}
      </p>
    </div>
  );
}